/**
 * QR밴드 코드 접두사입니다.
 * 전환 단계에 따라 값이 바뀌므로(기획서 §4.5) 문자열을 하드코딩하지 말고 이 상수를 참조하십시오.
 * ⚠️ 아래 3번 가드는 이 상수와 `LEGACY_BAND_PREFIX` 를 **둘 다** 봅니다 —
 *    접두사가 교체된 뒤에도 미회수 물리 구 밴드에 대한 숫자패턴 합성 방어는 그대로 남아야 합니다.
 */
const BAND_PREFIX = 'ZLB_';

/**
 * 폐기 예정인 구 밴드 접두사입니다 (S8 이전 발행분).
 * 신규 발행·해석은 전부 `BAND_PREFIX` 를 쓰지만, **미회수 물리 밴드**가 남아 있는 한
 * 아래 3번 가드는 이 값도 함께 봐야 합니다 — 그래야 구 밴드가 섞인 스캔값에서
 * 숫자패턴 복원이 일어나 서명 0짜리 가짜 회원 QR 이 합성되는 것을 계속 막을 수 있습니다.
 */
const LEGACY_BAND_PREFIX = 'JRB_';

/**
 * 퍼센트 인코딩을 해제합니다. 잘못된 인코딩(`%E0%A4%A` 등)이면 원문을 그대로 돌려줍니다.
 * - `decodeURIComponent` 는 URIError 를 던지므로, 상위 URL 파싱 try 를 오염시키지 않도록 여기서 흡수합니다.
 * - 실패 시 원문 반환은 서버 파서(`qr_utils.py` 의 `unquote`)와 동일한 거동입니다.
 *   `unquote` 는 예외를 던지지 않고 잘못된 이스케이프를 원문 그대로 남기므로, 두 파서의 결과를 맞춥니다.
 */
const safeDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
};

/**
 * QR 스캔 값에서 JRG_(회원) / ZLB_(QR밴드) 코드를 추출
 * - Raw 형태: JRG_MEM... / ZLB_B... → 그대로 반환
 * - URL 형태: https://.../?qr=JRG_MEM...      → JRG_MEM... 추출
 *             https://.../band?code=ZLB_B...  → ZLB_B... 추출
 *             https://.../bands/ZLB_B...      → ZLB_B... 추출 (경로형, 밴드 전용)
 * - 패턴 복원: YYYYMMDD_NNNNNN 숫자 패턴 발견 시 JRG_MEM 복원
 *   (한글 IME/영문자 누락 등으로 URL이 깨져도 숫자 패턴으로 회원 식별)
 *
 * ⚠️ 추출 실패 시 반환값은 **`null` 규약**입니다(기획서 D7).
 *    소비처 `QRScanner.js` 가 `if (extracted)` 로만 가드하므로, 원본 문자열을 반환하도록 바꾸면
 *    임의 QR 이 그대로 회원 조회로 흘러갑니다. 서버 파서(실패 시 원본 반환)와의 비대칭은 의도된 설계입니다.
 */
export const extractQRCode = (scannedValue) => {
  if (!scannedValue) return null;

  // 1. 정상 JRG_(회원) / 밴드 코드
  if (scannedValue.startsWith('JRG_') || scannedValue.startsWith(BAND_PREFIX)) {
    return scannedValue;
  }

  // 2. URL에서 qr(회원) / code(밴드) 파라미터 추출
  try {
    const url = new URL(scannedValue);
    const qr = url.searchParams.get('qr');
    if (qr && qr.startsWith('JRG_')) {
      return qr;
    }
    const bandCode = url.searchParams.get('code');
    if (bandCode && bandCode.startsWith(BAND_PREFIX)) {
      return bandCode;
    }

    // 2-1. 쿼리 파라미터 추출에 실패하면 경로 마지막 세그먼트에서 밴드 코드를 추출합니다.
    //      예) https://zl-webapp-dev.nxithub.com/bands/ZLB_B0008000001X_xxxx → ZLB_B0008000001X_xxxx
    //      ⚠️ 밴드 접두사만 허용합니다 — 회원 QR(JRG_)은 `?qr=` 쿼리형만 존재하므로
    //         경로형까지 열어주면 공격면만 넓어집니다.
    //      ⚠️ 이 블록은 반드시 아래 3번 가드(includes → null)보다 **앞**에 있어야 합니다.
    //         가드가 먼저 걸리면 정상 URL형 밴드 QR이 전부 null 이 되어 스캔이 동작하지 않습니다.
    const lastSegment = url.pathname.split('/').filter(Boolean).pop();
    if (lastSegment) {
      const decodedSegment = safeDecodeURIComponent(lastSegment);
      if (decodedSegment.startsWith(BAND_PREFIX)) {
        return decodedSegment;
      }
    }
  } catch (e) {
    // URL 파싱 실패
  }

  // 3. 밴드 코드가 섞인 스캔값은 아래 숫자 패턴 복원을 건너뛴다.
  //    밴드 시리얼에도 8자리_6자리 숫자열이 우연히 포함될 수 있고, 복원을 허용하면
  //    서명 0짜리 가짜 회원 QR(JRG_MEM..._0000000000000000)이 합성되어 엉뚱한 회원으로 조회된다.
  //    ⚠️ startsWith 가 아니라 includes 로 판정 — 실제 스캔 원문은 URL(https://...) 이라
  //       startsWith 는 절대 참이 되지 않는다.
  //    ⚠️ 신규(ZLB_)와 미회수 구 밴드(JRB_)를 **둘 다** 본다 — 구 밴드가 물리적으로 남아 있는 한
  //       한쪽만 보면 나머지에서 가짜 회원 QR 합성이 뚫린다.
  if (scannedValue.includes(BAND_PREFIX) || scannedValue.includes(LEGACY_BAND_PREFIX)) {
    return null;
  }

  // 4. 숫자 패턴으로 복원: YYYYMMDD_NNNNNN (8자리날짜_6자리시퀀스)
  //    한글 IME, 영문 누락, URL 깨짐 등 어떤 상태든 숫자 패턴만 있으면 복원
  const match = scannedValue.match(/(\d{8}_\d{6})/);
  if (match) {
    return 'JRG_MEM' + match[1] + '_0000000000000000';
  }

  return null;
};

/**
 * 여권 QR 접두사입니다 (설계 2026-08-25 §4.1 — `ZLP_{serial}_{hmac16}`).
 * 🔴 위 `extractQRCode` 의 접두사 집합에 **넣지 않습니다** — 아래 설명 참조.
 */
const PASSPORT_PREFIX = 'ZLP_';

/**
 * QR 스캔 값에서 **여권 코드(`ZLP_`)만** 추출합니다 (S17 / 기획서 §4.12.4 · C19).
 * - Raw 형태 : `ZLP_P0004000001P_349e...`            → 그대로 반환
 * - URL 형태 : `https://.../passports/ZLP_...`       → 마지막 경로 세그먼트 반환
 * - 그 외 전부 `null` (회원 QR·밴드 QR·임의 문자열 포함)
 *
 * 🔴 **왜 `extractQRCode` 를 넓히지 않고 함수를 새로 만드는가.**
 *    그 함수는 7개 파일이 직접 호출하고 `QRScanner` 를 통해 8개 페이지가 간접 소비합니다.
 *    거기에 `ZLP_` 를 통과시키면 그 화면들이 여권 QR 을 서버로 보내기 시작해,
 *    지금까지 "파싱 실패 안내"로 끝나던 스캔이 **서버 오류 응답 경로**로 바뀝니다.
 *    (퀘스트는 파싱 실패를 전용 안내판으로 처리 중입니다.)
 *
 * 🔴 **숫자패턴(`\d{8}_\d{6}`) 복원 로직을 태우지 않습니다.**
 *    그 복원은 서명이 0 인 가짜 회원 QR(`JRG_MEM..._0000000000000000`)을 합성합니다.
 *    밴드가 위 3번 가드로 막아 둔 구멍을 여권 파서에서 다시 열지 않습니다(§4.1 R-3).
 *
 * 🔴 **쿼리형(`?qr=`·`?code=`)을 보지 않습니다.** 여권 URL 은 경로형으로만 발행되므로
 *    (`build_passport_url`) 쿼리형까지 받아 주면 공격면만 넓어집니다.
 *    서버 `passport_qr_service.normalize_passport_code` 와 같은 판단입니다.
 *
 * ⚠️ 추출 실패 시 반환값은 **`null` 규약**입니다 — `QRScanner` 가 `if (extracted)` 로만
 *    가드하므로 원문을 돌려주면 임의 QR 이 그대로 서버 조회로 흘러갑니다.
 *
 * @param {*} scannedValue 스캔 원문(문자열이 아니면 즉시 null)
 * @returns {string|null} 여권 코드 또는 null
 */
export const extractPassportQRCode = (scannedValue) => {
  // 문자열이 아니면(숫자·null·undefined) 파싱 대상이 아닙니다.
  if (typeof scannedValue !== 'string') return null;

  // USB 리더기가 개행을 붙여 보내는 경우가 있어 양끝 공백을 먼저 걷어냅니다.
  const value = scannedValue.trim();
  if (!value) return null;

  // 1. 원문
  if (value.startsWith(PASSPORT_PREFIX)) {
    return value;
  }

  // 2. URL 경로형 — 마지막 세그먼트만 봅니다.
  try {
    const url = new URL(value);
    const lastSegment = url.pathname.split('/').filter(Boolean).pop();
    if (lastSegment) {
      const decoded = safeDecodeURIComponent(lastSegment);
      if (decoded.startsWith(PASSPORT_PREFIX)) {
        return decoded;
      }
    }
  } catch (e) {
    // URL 파싱 실패 — 아래 null 로 떨어집니다.
  }

  return null;
};

/**
 * **퀘스트 화면 전용** 스캔 파서입니다 — 회원(`JRG_`) · 밴드(`ZLB_`) · 여권(`ZLP_`) 3종 허용.
 *
 * 권위: docs/2026-09-01-passport-qr-mission-design.md §4.7 / dev-steps S10
 *
 * 🔴 **왜 `extractQRCode` 를 넓히지 않고 합성하는가.**
 *    `extractQRCode` 는 위 9개 소스가 직접·간접(`QRScanner`) 소비합니다. 거기에 `ZLP_` 를
 *    통과시키면 여권 취소·QR스티커·회원조회 화면까지 여권 QR 을 서버로 보내기 시작해,
 *    지금까지 "파싱 실패 안내"로 끝나던 스캔이 **서버 오류 응답 경로**로 바뀝니다.
 *    여권을 받아도 되는 곳은 퀘스트뿐이므로, 확장 범위를 이 함수를 쓰는 화면으로 **한정**합니다.
 *
 * 🔴 **비문자열 가드가 필수입니다.** `extractQRCode(123)` 은 `null` 이 아니라 **TypeError 를
 *    던집니다** — `!123` 이 false 라 `123.startsWith(...)` 로 진입하기 때문입니다.
 *    합성 함수가 그 지뢰를 흡수하지 않으면 퀘스트 스캔 콜백이 통째로 터집니다.
 *
 * 순서: 회원·밴드를 **먼저** 봅니다. 두 파서의 허용 집합은 서로 겹치지 않지만
 *       (`extractPassportQRCode` 는 `ZLP_` 접두사만 통과), 기존 거동을 우선한다는
 *       규칙을 코드 순서로 고정해 둡니다.
 *
 * ⚠️ 실패 시 `null` 규약은 그대로입니다(기획서 D7) — `QRScanner` 가 `if (extracted)` 로만
 *    가드하므로 원문을 돌려주면 임의 QR 이 그대로 서버 조회로 흘러갑니다.
 *
 * @param {*} raw 스캔 원문
 * @returns {string|null} 회원/밴드/여권 코드 또는 null
 */
export const extractQuestScanCode = (raw) => {
  if (typeof raw !== 'string') return null;
  return extractQRCode(raw) ?? extractPassportQRCode(raw);
};
